/**
 * Phase 0 · Infra-1 · P0.CLI.2 + P0.CLI.4
 *
 * In-call screen. Wires `useAgoraCall` to the `VideoStage`, `ControlsBar`, and
 * `CallTimer` primitives. Requests a token via `requestVideoToken` on mount.
 *
 * Route params:
 *   - channel:     cosmetic, equals the server-returned channel; not trusted
 *   - sessionType: passed to /api/video/token
 *   - sessionId:   passed to /api/video/token
 *   - mode:        "audio" | "video" (default: video)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  StyleSheet,
  Text,
  Alert,
  Platform,
  StatusBar,
  useWindowDimensions,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
} from 'react-native-reanimated';
import { useLocalSearchParams, useRouter } from 'expo-router';
import CallTimer from '@/components/video/CallTimer';
import IncallRemoteBackground from '@/components/video/incall/IncallRemoteBackground';
import IncallSelfView from '@/components/video/incall/IncallSelfView';
import IncallControlsBar from '@/components/video/incall/IncallControlsBar';
import LobbyFatalScreen from '@/components/video/lobby/LobbyFatalScreen';
import { useAgoraCall } from '@/hooks/useAgoraCall';
import { peekLinkTokenPayload, requestVideoToken, VideoTokenError } from '@/services/videoService';
import { useAuth } from '@/contexts/AuthContext';
import { consumeHandoff } from '@/services/videoCallHandoff';
import { resolveAvatarUrl } from '@/services/profilePhotoCacheService';

export default function VideoCallScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{
    channel: string;
    sessionType?: string;
    sessionId?: string;
    /**
     * Patient or sponsor signed link token, set by the deep-link handler in
     * app/_layout.tsx when the user opened the screen via /meet/<channel>?token=…
     * When present, we hand it to requestVideoToken which sends it as
     * X-Video-Link-Token instead of touching the Supabase session at all.
     */
    linkToken?: string;
    mode?: 'audio' | 'video';
  }>();
  const wantsVideo = (params.mode ?? 'video') === 'video';

  // AuthContext exposes the profile as `userProfile` — alias locally so the
  // rest of this file (which reads `profile?.avatar_url` etc.) actually
  // sees the loaded profile instead of always-undefined.
  const { session, user, userProfile: profile } = useAuth() as any;
  const call = useAgoraCall();
  const [channel, setChannel] = useState<string | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [connectedAt, setConnectedAt] = useState<number | null>(null);
  const [controlsBarHeight, setControlsBarHeight] = useState(96);
  const joinedRef = useRef(false);
  const insets = useSafeAreaInsets();
  const { width: screenW, height: screenH } = useWindowDimensions();

  const sessionType = String(params.sessionType ?? 'appointment');
  const linkToken = params.linkToken ? String(params.linkToken) : undefined;

  // For deep-link entries, sessionId can be omitted from the URL (the
  // /meet/<channel>?token=… surface only carries channel + token). The token's
  // `sid` claim IS the schedule id, so peek at it as a fallback. Server still
  // verifies cryptographically — this is a UX shortcut, not a security check.
  // useMemo so we don't re-decode the JWT on every render; the join effect
  // gates with joinedRef so we only ever consume the first stable value.
  const sessionId = useMemo(() => {
    const fromParams = String(params.sessionId ?? '');
    if (fromParams) return fromParams;
    if (linkToken) {
      const payload = peekLinkTokenPayload(linkToken);
      if (payload?.sid) return String(payload.sid);
    }
    return '';
  }, [params.sessionId, linkToken]);

  const cancelledRef = useRef(false);

  useEffect(() => {
    if (joinedRef.current) return;
    if (!sessionId) {
      setFatal('Sessão inválida.');
      return;
    }

    joinedRef.current = true;
    cancelledRef.current = false;

    (async () => {
      let creds: Awaited<ReturnType<typeof requestVideoToken>> | null = null;
      // Lobby handoff: when the user came through /video-call/lobby, the token
      // was already minted there + the engine was warmed for preview. Reuse
      // both so we don't re-burn an RTC token round-trip and we don't tear
      // down the camera between screens.
      const handoff = consumeHandoff(sessionId);
      try {
        if (handoff) {
          creds = handoff.creds;
        } else {
          creds = await requestVideoToken({
            sessionType,
            sessionId,
            // linkToken wins inside requestVideoToken — when set, the bearer
            // path (and the cold-start session probe) is skipped entirely.
            linkToken,
            accessToken: session?.access_token,
          });
        }
        if (cancelledRef.current) return;
        setChannel(creds.channel);
        await call.join(
          {
            appId: creds.appId,
            token: creds.token,
            channel: creds.channel,
            uid: creds.uid,
          },
          {
            video: wantsVideo,
            initialMicMuted: handoff?.initialMicMuted === true,
            initialCamOff: handoff?.initialCamOff === true,
          }
        );
        if (cancelledRef.current) {
          await call.leave();
        }
      } catch (err) {
        if (cancelledRef.current) return;
        // Diagnostic: the fatal screen text collapses ~10 error codes into one
        // line, which makes remote debugging painful. Log the raw cause so
        // device consoles surface exactly which branch tripped (link expired,
        // FORBIDDEN, WINDOW_CLOSED, Agora native join error, …).
        console.error('❌ [video-call] join failed', {
          phase: 'requestVideoToken-or-engine.join',
          sessionType,
          sessionId,
          hasLinkToken: !!linkToken,
          hasAccessToken: !!session?.access_token,
          errorName: err instanceof Error ? err.name : typeof err,
          errorMessage: err instanceof Error ? err.message : String(err),
          videoTokenErrorCode: err instanceof VideoTokenError ? err.code : undefined,
          videoTokenErrorStatus:
            err instanceof VideoTokenError ? (err as VideoTokenError & { status?: number }).status : undefined,
        });
        const msg =
          err instanceof VideoTokenError
            ? mapTokenError(err)
            : 'Não foi possível iniciar a chamada.';
        setFatal(msg);
      }
    })();

    return () => {
      cancelledRef.current = true;
    };

    // Intentionally run only once per mount. The hook owns engine cleanup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirror of "was ever connected" as a ref so end() can read fresh truth
  // even if the call connected after end()'s closure was created (race).
  const wasConnectedRef = useRef(false);

  useEffect(() => {
    if (call.phase === 'connected') {
      const firstConnect = !wasConnectedRef.current;
      wasConnectedRef.current = true;
      if (connectedAt === null) {
        setConnectedAt(Date.now());
      }
      if (firstConnect) {
        // PRD §4 — `meeting_joined` macro event. Native Agora video call —
        // meeting_source is the Elo platform that brokered the session,
        // meeting_type is 'video'. Off-dictionary keys (sessionType, channel,
        // mode, entry) are intentionally dropped per PRD §7.
        import('@/utils/clarityEvents')
          .then(({ logMeetingJoined }) =>
            logMeetingJoined({
              meeting_source: 'elo',
              meeting_type: 'video',
              meeting_id: sessionId,
            })
          )
          .catch(() => {});
      }
    }
  }, [call.phase, connectedAt, sessionType, sessionId, channel, wantsVideo, linkToken]);

  // Synchronous mutex — guards against the leave button firing twice in the
  // same event-loop tick before the navigation has a chance to commit.
  const endingRef = useRef(false);

  const exitToPrevious = useCallback(() => {
    // Deep-link entries have no history. Try back; if we can't, route home so
    // we never throw "GO_BACK action not handled by any navigator".
    // NOTE: home tab is `app/(tabs)/index.tsx` — `/(tabs)/home` does NOT
    // resolve and silently falls back to the last-active tab.
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/diary');
    }
  }, [router]);

  const end = useCallback(async () => {
    if (endingRef.current) return;
    endingRef.current = true;
    // Fire-and-forget Clarity event before tearing down. Duration is in
    // milliseconds (numeric); other fields are enums/IDs — no PII.
    const durationMs = connectedAt ? Date.now() - connectedAt : 0;
    import('@/utils/clarityEvents')
      .then(({ logClarityEvent }) =>
        logClarityEvent('meeting_left', {
          sessionType,
          sessionId,
          channel: channel ?? 'unknown',
          durationMs,
          everConnected: wasConnectedRef.current,
        })
      )
      .catch(() => {});
    // try/finally so a leave() throw still advances the UI instead of
    // stranding the user on a dead in-call screen.
    try {
      await call.leave();
    } catch (err) {
      console.warn('[video-call] leave() threw, continuing exit', err);
    }
    // Only prompt if the call actually connected. Read the ref (not the
    // captured connectedAt) so a connection that landed mid-leave still
    // qualifies for the CSAT prompt.
    if (!wasConnectedRef.current) {
      exitToPrevious();
      return;
    }
    // Full-screen post-call confirmation (Figma 6691:20555). Replaces the
    // earlier bottom sheet — that surface was retired with this commit.
    router.replace('/video-call/postcall');
  }, [call, exitToPrevious, router]);

  useEffect(() => {
    if (call.phase === 'failed' && !fatal) {
      // Same diagnostic story: the engine can fail post-token-mint (token
      // accepted but Agora rejected the join). Log so we can tell apart
      // server-side denials from native SDK failures next time.
      console.error('❌ [video-call] engine phase=failed after successful token request', {
        sessionType,
        sessionId,
      });
      setFatal('A conexão falhou. Tente novamente.');
    }
  }, [call.phase, fatal, sessionType, sessionId]);

  // ── render-only derivations & hooks ──
  // Must live ABOVE the `if (fatal) return` early-out: the first render runs
  // with fatal=null and executes them, so a later setFatal() must keep the
  // same hook count or React throws "Rendered fewer hooks than expected".
  const primaryRemote = call.remoteUsers[0] ?? null;
  // The "Aguardando…" copy disappears once the remote participant joins,
  // but the PiP self-view stays the full 160×200 throughout the call —
  // product preference: a stable card is easier to read than one that
  // resizes mid-call.
  const remoteJoined = !!primaryRemote;

  // Same avatar normalisation we do in the lobby — relative Supabase paths
  // need the storage URL prefix; OAuth users may only have it under
  // user_metadata. Without this, the off-state avatar falls through to the
  // bare Blue Blumine fill.
  const avatarUrl = useMemo(
    () =>
      resolveAvatarUrl(
        profile?.avatar_url ||
          user?.user_metadata?.avatar_url ||
          user?.user_metadata?.picture ||
          null,
      ),
    [profile?.avatar_url, user?.user_metadata?.avatar_url, user?.user_metadata?.picture],
  );

  const firstName = useMemo(() => {
    const full = (profile?.full_name || user?.user_metadata?.full_name || '').trim();
    if (!full) return '';
    return full.split(/\s+/)[0];
  }, [profile?.full_name, user?.user_metadata?.full_name]);

  const handleEndPress = useCallback(() => {
    Alert.alert(
      'Encerrar chamada?',
      'Você vai sair da videochamada agora.',
      [
        { text: 'Continuar', style: 'cancel' },
        { text: 'Encerrar', style: 'destructive', onPress: end },
      ]
    );
  }, [end]);

  if (fatal) {
    // Telemedicine P1.UI Phase B — share the lobby's "Não foi possível
    // conectar" surface (Figma 6684:19657) so the lobby + in-call fatal
    // states are visually identical. `fatal` carries the diagnostic
    // string (mapped from VideoTokenError or the engine failure path) —
    // we keep the Figma headline static and surface `fatal` as the body
    // copy so a "Sessão inválida." vs "A conexão falhou." still reads
    // accurately. CTA + close both call exitToPrevious — identical to
    // the old inline implementation's behaviour.
    return (
      <LobbyFatalScreen
        body={fatal}
        onExit={exitToPrevious}
      />
    );
  }

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" />

      {/* Layer 1 — full-screen background: remote video if present, else
          Blue Whale waiting backdrop with ambient blobs. */}
      <IncallRemoteBackground
        primaryRemote={primaryRemote}
        channel={channel ?? params.channel ?? ''}
      />

      {/* Layer 2 — top-center timer pill. */}
      <View style={[styles.timerRow, { top: Platform.OS === 'ios' ? 56 : 24 }]}>
        <CallTimer
          startedAt={connectedAt}
          status={
            call.phase === 'connecting'
              ? 'Conectando…'
              : call.phase === 'reconnecting'
                ? 'Reconectando…'
                : undefined
          }
        />
      </View>

      {/* Layer 3 — draggable PiP self-view. Absolutely positioned over the
          full container so it can be dragged to any corner freely. */}
      <DraggablePip
        topY={(Platform.OS === 'ios' ? 56 : 24) + 44}
        bottomY={screenH - insets.bottom - controlsBarHeight - PIP_H - PIP_MARGIN}
        rightX={screenW - PIP_MARGIN - PIP_W}
      >
        <IncallSelfView
          cameraEnabled={call.cameraEnabled}
          large
          firstName={firstName}
          avatarUrl={avatarUrl}
        />
      </DraggablePip>

      {/* Layer 4 — waiting label, pinned just below the timer pill. */}
      {!remoteJoined && (
        <Text
          style={[
            styles.waitingText,
            { top: (Platform.OS === 'ios' ? 56 : 24) + 44 + 8 },
          ]}
        >
          Aguardando o outro participante…
        </Text>
      )}

      {/* Layer 5 — bottom controls bar. */}
      <SafeAreaView style={styles.bottomStack} edges={['bottom']}>
        <View
          style={styles.controlsRow}
          onLayout={e => setControlsBarHeight(e.nativeEvent.layout.height)}
        >
          <IncallControlsBar
            micEnabled={call.micEnabled}
            cameraEnabled={call.cameraEnabled}
            canFlipCamera={call.cameraEnabled}
            audioLevel={call.localAudioLevel}
            onToggleMic={call.toggleMic}
            onToggleCamera={call.toggleCamera}
            onSwitchCamera={call.switchCamera}
            onEnd={handleEndPress}
          />
        </View>
      </SafeAreaView>
    </View>
  );
}

// ─── PiP constants ───────────────────────────────────────────────────────────
const PIP_W = 160;
const PIP_H = 200;
const PIP_MARGIN = 24;
const PIP_SPRING = { damping: 22, stiffness: 240, mass: 0.8 };

/**
 * Wraps any child in an absolutely-positioned, draggable container that snaps
 * to the nearest of the four screen corners on pointer-up.
 *
 * Props are plain numbers so the Reanimated worklet can capture them safely.
 * The parent passes fresh values whenever the layout changes (e.g. after the
 * controls bar is measured), which re-creates the gesture with updated corners.
 */
function DraggablePip({
  topY,
  bottomY,
  rightX,
  children,
}: {
  topY: number;
  bottomY: number;
  rightX: number;
  children: React.ReactNode;
}) {
  // Default corner: bottom-left (matches existing Figma placement).
  const posX = useSharedValue(PIP_MARGIN);
  const posY = useSharedValue(bottomY);
  const startX = useSharedValue(PIP_MARGIN);
  const startY = useSharedValue(PIP_MARGIN);

  const pan = Gesture.Pan()
    .onStart(() => {
      startX.value = posX.value;
      startY.value = posY.value;
    })
    .onUpdate((e) => {
      posX.value = startX.value + e.translationX;
      posY.value = startY.value + e.translationY;
    })
    .onEnd(() => {
      // Find nearest corner centre and spring to it.
      const cx = posX.value + PIP_W / 2;
      const cy = posY.value + PIP_H / 2;

      // Four corners: TL, TR, BL, BR
      const cxs = [PIP_MARGIN, rightX, PIP_MARGIN, rightX];
      const cys = [topY, topY, bottomY, bottomY];

      let nearestX = PIP_MARGIN;
      let nearestY = bottomY;
      let minDist = Infinity;
      for (let i = 0; i < 4; i++) {
        const dx = cx - (cxs[i] + PIP_W / 2);
        const dy = cy - (cys[i] + PIP_H / 2);
        const d = dx * dx + dy * dy;
        if (d < minDist) {
          minDist = d;
          nearestX = cxs[i];
          nearestY = cys[i];
        }
      }

      posX.value = withSpring(nearestX, PIP_SPRING);
      posY.value = withSpring(nearestY, PIP_SPRING);
    });

  const animStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: posX.value },
      { translateY: posY.value },
    ],
  }));

  return (
    <GestureDetector gesture={pan}>
      <Animated.View style={[styles.pipWrapper, animStyle]}>
        {children}
      </Animated.View>
    </GestureDetector>
  );
}

function mapTokenError(err: VideoTokenError): string {
  switch (err.code) {
    case 'not_authenticated':
      return 'Você precisa estar conectado para entrar na chamada.';
    case 'patient_link_expired':
    case 'sponsor_link_expired':
      return 'Este link de chamada expirou. Abra o convite mais recente do seu e-mail.';
    case 'invalid_patient_link':
    case 'invalid_sponsor_link':
      return 'Link de chamada inválido.';
    case 'CANCELED':
      return 'Este compromisso foi cancelado.';
    case 'WINDOW_CLOSED':
      return 'Esta chamada ainda não está disponível ou já foi encerrada.';
    case 'NOT_FOUND':
      return 'Compromisso não encontrado.';
    case 'FORBIDDEN':
      return 'Você não tem permissão para entrar nesta chamada.';
    case 'agora_not_configured':
      return 'Serviço de vídeo indisponível. Tente novamente mais tarde.';
    default:
      return 'Não foi possível iniciar a chamada.';
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#002E49', // Blue Whale — visible through any gaps before the bg layer paints
  },
  timerRow: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  // Wraps the waiting message + self-view + controls bar so the bottom
  // half of the screen is laid out as a single column. The absolute
  // positioning keeps it pinned to the bottom edge regardless of remote
  // video aspect ratio.
  bottomStack: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
  },
  waitingText: {
    position: 'absolute',
    left: 0,
    right: 0,
    color: '#FFFFFF',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6,
    textAlign: 'center',
    paddingHorizontal: 24,
  },
  pipWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 10,
  },
  controlsRow: {
    paddingHorizontal: 24,
    paddingBottom: 24,
  },
});
